import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MessageSquare, 
  AlertTriangle,
  CheckCircle,
  ChevronRight,
  Upload, 
  FileText,
  Check,
  Camera,
  CreditCard,
  X,
  Clock,
  Database,
  Server,
  Terminal,
  ArrowRight,
  Loader,
  Copy,
  Info,
  Layout,
  Type,
  ImageIcon
} from 'lucide-react';
import { app, functions as configFunctions, storage } from '../../firebaseConfig';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Initialise Firebase functions
const functions = getFunctions(app, 'europe-west2');
const createSignupFunction = httpsCallable(functions, 'createSignup');
const getDocumentUploadUrlFunction = httpsCallable(functions, 'getDocumentUploadUrl');

// ThrongletPipeline component
const ThrongletPipeline = ({ stage, processingStage = 0, isSubmitting = false }) => {
  // Generate random ID for visual elements
  const generateRandomId = (length = 10) => {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    return Array.from({length}, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  };

  // SVG illustrations for each stage - simplified versions
  const renderThronglet = (state) => {
    switch(state) {
      case "registration":
        return (
          <svg width="100" height="100" viewBox="0 0 60 60">
            <rect x="20" y="20" width="20" height="20" fill="#FFD700" rx="10">
              <animate attributeName="opacity" values="0.6;1;0.6" dur="2s" repeatCount="indefinite" />
            </rect>
            <circle cx="30" cy="30" r="15" fill="transparent" stroke="#7FFFD4" strokeWidth="2" strokeDasharray="5,2">
              <animateTransform
                attributeName="transform"
                type="rotate"
                from="0 30 30"
                to="360 30 30"
                dur="8s"
                repeatCount="indefinite"
              />
            </circle>
            <circle cx="25" cy="15" r="1.5" fill="#7FFFD4">
              <animate attributeName="cy" values="15;30;15" dur="2s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="1;0;1" dur="2s" repeatCount="indefinite" />
            </circle>
            <circle cx="35" cy="15" r="1.5" fill="#7FFFD4">
              <animate attributeName="cy" values="15;30;15" dur="2.3s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="1;0;1" dur="2.3s" repeatCount="indefinite" />
            </circle>
          </svg>
        );
      case "upload":
        return (
          <svg width="100" height="100" viewBox="0 0 60 60">
            <rect x="15" y="20" width="30" height="25" fill="#FFD700" rx="5" />
            <rect x="20" y="45" width="8" height="8" fill="#FFD700" />
            <rect x="32" y="45" width="8" height="8" fill="#FFD700" />
            <rect x="20" y="15" width="6" height="10" fill="#FFD700" />
            <rect x="34" y="15" width="6" height="10" fill="#FFD700" />
            <rect x="18" y="30" width="24" height="10" fill="#ADD8E6" />
            <circle cx="25" cy="25" r="2" fill="black">
              <animate attributeName="r" values="2;1.5;2" dur="3s" repeatCount="indefinite" />
            </circle>
            <circle cx="35" cy="25" r="2" fill="black">
              <animate attributeName="r" values="2;1.5;2" dur="3s" repeatCount="indefinite" />
            </circle>
            
            <rect x="10" y="10" width="8" height="10" fill="#FF6347" opacity="0.8">
              <animate attributeName="y" values="10;30;60" dur="2s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.8;0.6;0" dur="2s" repeatCount="indefinite" />
            </rect>
            <rect x="42" y="5" width="8" height="10" fill="#40E0D0" opacity="0.8">
              <animate attributeName="y" values="5;25;60" dur="2.5s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.8;0.6;0" dur="2.5s" repeatCount="indefinite" />
            </rect>
          </svg>
        );
      case "coding":
        return (
          <svg width="100" height="100" viewBox="0 0 60 60">
            <rect x="15" y="20" width="30" height="25" fill="#FFD700" rx="5" />
            <rect x="20" y="45" width="8" height="8" fill="#FFD700" />
            <rect x="32" y="45" width="8" height="8" fill="#FFD700" />
            <rect x="20" y="15" width="6" height="10" fill="#FFD700" />
            <rect x="34" y="15" width="6" height="10" fill="#FFD700" />
            <rect x="18" y="30" width="24" height="10" fill="#ADD8E6" />
            <circle cx="25" cy="25" r="2" fill="black" />
            <circle cx="35" cy="25" r="2" fill="black" />
            
            <g>
              <animateTransform
                attributeName="transform"
                type="translate"
                values="0,0; 0,1; 0,0; 0,-1; 0,0"
                dur="1s"
                repeatCount="indefinite"
              />
              <text x="23" y="37" fill="#0000CD" fontSize="6" fontFamily="monospace">{"<>"}</text>
              <text x="30" y="37" fill="#006400" fontSize="6" fontFamily="monospace">{"{ }"}</text>
            </g>
            
            <text x="12" y="20" fill="#7FFFD4" fontSize="5" opacity="0.7" fontFamily="monospace">
              <animate attributeName="y" values="20;10;20" dur="4s" repeatCount="indefinite" />
              {"function()"}
            </text>
            <text x="40" y="25" fill="#7FFFD4" fontSize="5" opacity="0.7" fontFamily="monospace">
              <animate attributeName="y" values="25;30;25" dur="3s" repeatCount="indefinite" />
              {"return"}
            </text>
          </svg>
        );
      case "transfer":
        return (
          <svg width="100" height="100" viewBox="0 0 60 60">
            <rect x="15" y="20" width="30" height="25" fill="#FFD700" rx="5">
              <animateTransform
                attributeName="transform"
                type="translate"
                values="-20,0; 0,0; 20,0; 0,0; -20,0"
                dur="7s"
                repeatCount="indefinite"
              />
            </rect>
            <rect x="20" y="45" width="8" height="8" fill="#FFD700">
              <animateTransform
                attributeName="transform"
                type="translate"
                values="-20,0; 0,0; 20,0; 0,0; -20,0"
                dur="7s"
                repeatCount="indefinite"
              />
            </rect>
            <rect x="32" y="45" width="8" height="8" fill="#FFD700">
              <animateTransform
                attributeName="transform"
                type="translate"
                values="-20,0; 0,0; 20,0; 0,0; -20,0"
                dur="7s"
                repeatCount="indefinite"
              />
            </rect>
            <rect x="20" y="15" width="6" height="10" fill="#FFD700">
              <animateTransform
                attributeName="transform"
                type="translate"
                values="-20,0; 0,0; 20,0; 0,0; -20,0"
                dur="7s"
                repeatCount="indefinite"
              />
            </rect>
            <rect x="34" y="15" width="6" height="10" fill="#FFD700">
              <animateTransform
                attributeName="transform"
                type="translate"
                values="-20,0; 0,0; 20,0; 0,0; -20,0"
                dur="7s"
                repeatCount="indefinite"
              />
            </rect>
            <rect x="18" y="30" width="24" height="10" fill="#ADD8E6">
              <animateTransform
                attributeName="transform"
                type="translate"
                values="-20,0; 0,0; 20,0; 0,0; -20,0"
                dur="7s"
                repeatCount="indefinite"
              />
            </rect>
            <circle cx="25" cy="25" r="2" fill="black">
              <animateTransform
                attributeName="transform"
                type="translate"
                values="-20,0; 0,0; 20,0; 0,0; -20,0"
                dur="7s"
                repeatCount="indefinite"
              />
            </circle>
            <circle cx="35" cy="25" r="2" fill="black">
              <animateTransform
                attributeName="transform"
                type="translate"
                values="-20,0; 0,0; 20,0; 0,0; -20,0"
                dur="7s"
                repeatCount="indefinite"
              />
            </circle>
            
            {/* Moving data stream */}
            <g>
              <line x1="5" y1="35" x2="55" y2="35" stroke="#7FFFD4" strokeWidth="1" strokeDasharray="3,3">
                <animate attributeName="stroke-dashoffset" values="0;24" dur="2s" repeatCount="indefinite" />
              </line>
              <line x1="5" y1="39" x2="55" y2="39" stroke="#7FFFD4" strokeWidth="1" strokeDasharray="3,3">
                <animate attributeName="stroke-dashoffset" values="0;-24" dur="2s" repeatCount="indefinite" />
              </line>
            </g>
          </svg>
        );
      case "functions":
        return (
          <svg width="100" height="100" viewBox="0 0 60 60">
            <rect x="15" y="20" width="30" height="25" fill="#FFD700" rx="5" />
            <rect x="20" y="45" width="8" height="8" fill="#FFD700" />
            <rect x="32" y="45" width="8" height="8" fill="#FFD700" />
            <rect x="20" y="15" width="6" height="10" fill="#FFD700" />
            <rect x="34" y="15" width="6" height="10" fill="#FFD700" />
            <rect x="18" y="30" width="24" height="10" fill="#4169E1">
              <animate attributeName="fill" values="#4169E1;#00BFFF;#4169E1" dur="1.5s" repeatCount="indefinite" />
            </rect>
            <circle cx="25" cy="25" r="2" fill="black" />
            <circle cx="35" cy="25" r="2" fill="black" />
            
            {/* Processing effects */}
            <circle cx="30" cy="30" r="25" fill="transparent" stroke="#7FFFD4" strokeWidth="2" strokeDasharray="3,3">
              <animateTransform
                attributeName="transform"
                type="rotate"
                from="0 30 30"
                to="360 30 30"
                dur="3s"
                repeatCount="indefinite"
              />
            </circle>
            
            {/* Function activation pulses */}
            <circle cx="30" cy="30" r="15" fill="transparent" stroke="#FF4500" strokeWidth="1.5" opacity="0.7">
              <animate attributeName="r" values="15;30;15" dur="2s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.7;0;0.7" dur="2s" repeatCount="indefinite" />
            </circle>
            
            {/* Cloud function text */}
            <text x="10" y="12" fill="#7FFFD4" fontSize="5" fontFamily="monospace" opacity="0.8">
              <animate attributeName="opacity" values="0.8;1;0.8" dur="2s" repeatCount="indefinite" />
              {"brainCache()"}
            </text>
          </svg>
        );
      case "database":
        return (
          <svg width="100" height="100" viewBox="0 0 60 60">
            <rect x="15" y="20" width="30" height="25" fill="#FFD700" rx="5" />
            <rect x="20" y="45" width="8" height="8" fill="#FFD700" />
            <rect x="32" y="45" width="8" height="8" fill="#FFD700" />
            <rect x="20" y="15" width="6" height="10" fill="#FFD700" />
            <rect x="34" y="15" width="6" height="10" fill="#FFD700" />
            <rect x="18" y="30" width="24" height="10" fill="#4169E1" />
            <circle cx="25" cy="25" r="2" fill="black" />
            <circle cx="35" cy="25" r="2" fill="black" />
            
            {/* Animated database */}
            <g>
              <ellipse cx="42" cy="20" rx="8" ry="4" fill="#1E90FF" stroke="#000" strokeWidth="1">
                <animate attributeName="ry" values="4;3.5;4" dur="1.5s" repeatCount="indefinite" />
              </ellipse>
              <rect x="34" y="20" width="16" height="8" fill="#1E90FF" stroke="#000" strokeWidth="1" />
              <ellipse cx="42" cy="28" rx="8" ry="4" fill="#1E90FF" stroke="#000" strokeWidth="1">
                <animate attributeName="ry" values="4;4.5;4" dur="1.5s" repeatCount="indefinite" />
              </ellipse>
            </g>
            
            {/* Status update lines */}
            <line x1="28" y1="15" x2="42" y2="22" stroke="#7FFFD4" strokeWidth="1" strokeDasharray="2,1">
              <animate attributeName="opacity" values="0;1;0" dur="1s" repeatCount="indefinite" />
            </line>
            
            {/* Status text */}
            <text x="5" y="55" fill="#FFA500" fontSize="5" fontFamily="monospace" opacity="0.9">
              <animate attributeName="opacity" values="0.9;0.7;0.9" dur="2s" repeatCount="indefinite" />
              {"status → processing_started"}
            </text>
          </svg>
        );
      case "pipeline":
        return (
          <svg width="100" height="100" viewBox="0 0 60 60">
            <rect x="15" y="15" width="30" height="25" fill="#FFD700" rx="5">
              <animate attributeName="width" values="30;31;30" dur="1s" repeatCount="indefinite" />
              <animate attributeName="height" values="25;26;25" dur="1.2s" repeatCount="indefinite" />
            </rect>
            <rect x="20" y="40" width="8" height="8" fill="#FFD700" />
            <rect x="32" y="40" width="8" height="8" fill="#FFD700" />
            <rect x="20" y="10" width="6" height="10" fill="#FFD700" />
            <rect x="34" y="10" width="6" height="10" fill="#FFD700" />
            <rect x="18" y="25" width="24" height="10" fill="#4169E1">
              <animate attributeName="fill" values="#4169E1;#00BFFF;#4169E1" dur="2s" repeatCount="indefinite" />
            </rect>
            <circle cx="25" cy="20" r="2" fill="black" />
            <circle cx="35" cy="20" r="2" fill="black" />
            
            {/* Evolving particles and effects */}
            <circle cx="15" cy="15" r="2" fill="#7FFFD4">
              <animate attributeName="cx" values="15;45;15" dur="3s" repeatCount="indefinite" />
              <animate attributeName="cy" values="15;45;15" dur="3s" repeatCount="indefinite" />
            </circle>
            <circle cx="45" cy="15" r="2" fill="#7FFFD4">
              <animate attributeName="cx" values="45;15;45" dur="3s" repeatCount="indefinite" />
              <animate attributeName="cy" values="15;45;15" dur="3s" repeatCount="indefinite" />
            </circle>
            <circle cx="30" cy="30" r="2" fill="#FF4500">
              <animate attributeName="cx" values="30;20;40;30" dur="4s" repeatCount="indefinite" />
              <animate attributeName="cy" values="30;40;20;30" dur="4s" repeatCount="indefinite" />
            </circle>
            
            {/* Pipeline steps indicators */}
            <rect x="10" y="48" width="10" height="3" fill="#7CFC00" opacity="0.8">
              <animate attributeName="width" values="0;10" dur="6s" repeatCount="1" />
            </rect>
            <rect x="25" y="48" width="10" height="3" fill="#7CFC00" opacity="0.8">
              <animate attributeName="width" values="0;10" dur="6s" begin="2s" repeatCount="1" />
            </rect>
            <rect x="40" y="48" width="10" height="3" fill="#7CFC00" opacity="0.8">
              <animate attributeName="width" values="0;10" dur="6s" begin="4s" repeatCount="1" />
            </rect>
          </svg>
        );
      case "complete":
        return (
          <svg width="100" height="100" viewBox="0 0 60 60">
            <rect x="12" y="15" width="36" height="25" fill="#FFD700" rx="5">
              <animate attributeName="fill" values="#FFD700;#FFDF00;#FFD700" dur="3s" repeatCount="indefinite" />
            </rect>
            <rect x="17" y="40" width="10" height="8" fill="#FFD700" />
            <rect x="33" y="40" width="10" height="8" fill="#FFD700" />
            <rect x="17" y="5" width="8" height="12" fill="#FFD700" />
            <rect x="35" y="5" width="8" height="12" fill="#FFD700" />
            <rect x="16" y="25" width="28" height="10" fill="#4169E1">
              <animate attributeName="fill" values="#4169E1;#00BFFF;#4169E1" dur="3s" repeatCount="indefinite" />
            </rect>
            <circle cx="25" cy="20" r="3" fill="black" />
            <circle cx="35" cy="20" r="3" fill="black" />
            
            {/* Crown with animation */}
            <polygon points="30,8 35,12 25,12" fill="#FFD700" stroke="#DAA520" strokeWidth="1">
              <animate attributeName="fill" values="#FFD700;#FFC125;#FFD700" dur="2s" repeatCount="indefinite" />
              <animateTransform
                attributeName="transform"
                type="translate"
                values="0,-1; 0,0; 0,-1"
                dur="2s"
                repeatCount="indefinite"
              />
            </polygon>
            
            {/* Happy expression */}
            <path d="M25,30 Q30,35 35,30" fill="transparent" stroke="black" strokeWidth="1.5" />
            
            {/* Checkmark animation */}
            <path d="M20,50 L28,58 L40,40" fill="none" stroke="#7CFC00" strokeWidth="2" strokeDasharray="40">
              <animate attributeName="stroke-dashoffset" values="40;0" dur="1s" repeatCount="1" />
            </path>
          </svg>
        );
      default:
        return null;
    }
  };

  // Get animation state based on stages
  const getStageState = () => {
    if (isSubmitting) {
      switch(processingStage) {
        case 0: return "transfer";    // Files being transferred
        case 1: return "functions";   // Cloud Functions triggered
        case 2: return "database";    // Firestore update
        case 3: return "pipeline";    // Vertex AI Pipeline processing
        case 4: return "complete";    // Setup complete
        default: return "transfer";
      }
    } else {
      switch(stage) {
        case 1: return "registration";
        case 2: return "upload";
        case 3: return "coding";
        case 4: return "functions";
        case 5: return "complete";
        default: return "registration";
      }
    }
  };

  // Get stage description
  const getStageName = () => {
    if (isSubmitting) {
      switch(processingStage) {
        case 0: return "File Transfer Phase";
        case 1: return "Cloud Functions Phase";
        case 2: return "Firestore Update Phase";
        case 3: return "AI Pipeline Phase";
        case 4: return "Setup Complete!";
        default: return "Processing...";
      }
    } else {
      switch(stage) {
        case 1: return "Registration Phase";
        case 2: return "Document Upload Phase";
        case 3: return "Code Generation Phase";
        case 4: return "Agreement Phase";
        case 5: return "Setup Complete!";
        default: return "Registration Phase";
      }
    }
  };

  // Get stage description
  const getStageDescription = () => {
    if (isSubmitting) {
      switch(processingStage) {
        case 0: return "Securely uploading your documents...";
        case 1: return "Preparing your data for training...";
        case 2: return "Saving your settings...";
        case 3: return "AI pipeline analysing your content to train Nia...";
        case 4: return "Your AI Sales Assistant is ready to train!";
        default: return "Setting things up for you...";
      }
    } else {
      switch(stage) {
        case 1: return "Setting up your AI assistant...";
        case 2: return "Upload your files to train Nia...";
        case 3: return "Customising your AI assistant’s chat window to match your brand....";
        case 4: return "Almost there! Review and confirm...";
        case 5: return "Your AI Sales Assistant is ready to train!";
        default: return "Setting up your AI assistant...";
      }
    }
  };

  return (
    <div className={`flex flex-col items-center bg-slate-800/30 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 h-full ${isSubmitting ? 'border-blue-500/30 shadow-lg shadow-blue-500/10' : ''}`}>
      <div className="text-center mb-4">
        <h3 className="text-xl font-semibold text-blue-400">Thronglet Pipeline</h3>
        <p className="text-sm text-slate-300">Watch Your Nia Setup Progress</p>
      </div>
      
      <div className={`my-6 scale-100 transition-all duration-700 ${isSubmitting ? 'scale-110' : ''}`}>
        {renderThronglet(getStageState())}
      </div>
      
      <div className="mt-4 text-center">
        <h4 className="text-lg font-medium text-white mb-2">
          {getStageName()}
        </h4>
        <p className="text-sm text-slate-300">
          {getStageDescription()}
        </p>
      </div>
      
      {isSubmitting && (
        <div className="w-full max-w-md mx-auto mt-6 bg-blue-900/20 rounded-lg p-3 border border-blue-500/20">
          <div className="flex justify-between items-center">
            <span className="text-sm text-blue-300">Processing</span>
            <span className="text-sm text-blue-300">{Math.min(processingStage + 1, 5)}/5 steps</span>
          </div>
          <div className="w-full bg-slate-700 h-2 rounded-full mt-2">
            <div 
              className="bg-blue-500 h-2 rounded-full transition-all duration-500"
              style={{ width: `${Math.min((processingStage + 1) * 25, 100)}%` }}
            ></div>
          </div>
          
          <div className="flex justify-between mt-6 text-xs text-slate-400">
            <div className={`flex flex-col items-center ${processingStage >= 0 ? 'text-blue-300' : ''}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center mb-1 ${processingStage >= 0 ? 'bg-blue-500' : 'bg-slate-700'}`}>
                {processingStage > 0 ? <Check className="w-3 h-3" /> : 1}
              </div>
              <span>Transfer</span>
            </div>
            
            <div className={`flex flex-col items-center ${processingStage >= 1 ? 'text-blue-300' : ''}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center mb-1 ${processingStage >= 1 ? 'bg-blue-500' : 'bg-slate-700'}`}>
                {processingStage > 1 ? <Check className="w-3 h-3" /> : 2}
              </div>
              <span>Functions</span>
            </div>
            
            <div className={`flex flex-col items-center ${processingStage >= 2 ? 'text-blue-300' : ''}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center mb-1 ${processingStage >= 2 ? 'bg-blue-500' : 'bg-slate-700'}`}>
                {processingStage > 2 ? <Check className="w-3 h-3" /> : 3}
              </div>
              <span>Database</span>
            </div>
            
            <div className={`flex flex-col items-center ${processingStage >= 3 ? 'text-blue-300' : ''}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center mb-1 ${processingStage >= 3 ? 'bg-blue-500' : 'bg-slate-700'}`}>
                {processingStage > 3 ? <Check className="w-3 h-3" /> : 4}
              </div>
              <span>Pipeline</span>
            </div>
            
            <div className={`flex flex-col items-center ${processingStage >= 4 ? 'text-blue-300' : ''}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center mb-1 ${processingStage >= 4 ? 'bg-blue-500' : 'bg-slate-700'}`}>
                {processingStage > 4 ? <Check className="w-3 h-3" /> : 5}
              </div>
              <span>Complete</span>
            </div>
          </div>
        </div>
      )}
      
      <div className="mt-6 w-full">
        <div className="bg-slate-700/50 rounded-lg p-3 text-xs text-cyan-300 font-mono border border-slate-600/50">
          <div className="flex justify-between">
            <span>Status: {
              isSubmitting ? 
                (processingStage === 0 ? "Secure File Transfer" :
                 processingStage === 1 ? "Executing Cloud Functions" :
                 processingStage === 2 ? "Updating Records" :
                 processingStage === 3 ? "Processing AI Pipeline" :
                 "Setup Complete") :
                (stage === 1 ? "Waiting for Business Information" :
                 stage === 2 ? "Waiting for your sales materials" :
                 stage === 3 ? "Customisation " :
                 stage === 4 ? "Accept terms to continue" :
                 "Complete")
            }</span>
          </div>
          
          {(stage === 2 || (isSubmitting && processingStage === 0)) && (
            <div className="mt-2">
              <div>Uploading your documents...</div>
              <div className="flex items-center mt-1">
                <div className="w-full bg-slate-800 rounded-full h-1.5">
                  <div className="bg-blue-400 h-1.5 rounded-full animate-[progress_3s_ease-in-out_forwards]" style={isSubmitting ? {width: '100%'} : {width: '75%'}}></div>
                </div>
                <span className="ml-2">{isSubmitting ? '100%' : '40%'}</span>
              </div>
            </div>
          )}
          
          {(stage === 3 || (isSubmitting && processingStage === 1)) && (
            <div className="mt-2">
              <div>Creating embed code:</div>
              <div className="mt-1 overflow-hidden">
                <span>{'<script src="https://gclouds-gemini.web.app/embed.js">'}</span>
              </div>
            </div>
          )}
          
          {(isSubmitting && (processingStage === 2 || processingStage === 3)) && (
            <div className="mt-2">
              <div>Pipeline status: <span className="text-yellow-300 animate-pulse">RUNNING</span></div>
              <div className="grid grid-cols-3 gap-1 mt-1">
                <div className="text-green-300 bg-green-900/20 px-1 rounded">Step 1 ✓</div>
                <div className={`${processingStage >= 3 ? 'text-green-300 bg-green-900/20' : 'text-yellow-300 bg-yellow-900/20 animate-pulse'} px-1 rounded`}>
                  Step 2 {processingStage >= 3 ? '✓' : '⟳'}
                </div>
                <div className={`${processingStage >= 3 ? 'text-yellow-300 bg-yellow-900/20 animate-pulse' : 'text-slate-400'} px-1 rounded`}>
                  Step 3 {processingStage >= 3 ? '⟳' : '⧖'}
                </div>
              </div>
            </div>
          )}
          
          {(stage === 5 || (isSubmitting && processingStage === 4)) && (
            <div className="mt-2">
              <div>Learning your content: <span className="text-green-300">Initiated</span></div>
              <div className="grid grid-cols-2 gap-1 mt-1">
                <div>Engine: <span className="text-green-300">Initiated</span></div>
                <div>AI brain: <span className="text-green-300">Ready</span></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Main NiaSignup Component with ThrongletPipeline Integration
const NiaSignupAnimated = () => {
  const navigate = useNavigate();
  const scriptRef = useRef(null);
  const [activeStep, setActiveStep] = useState(1);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [formError, setFormError] = useState('');
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [termsError, setTermsError] = useState(false);
  const [processingStage, setProcessingStage] = useState(0);

  // Form fields
  const [formData, setFormData] = useState({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    website: '',
    industry: '',
    companySize: '',
    salesChallenges: '',
    customerId: '',
  });

  // Chat configuration with defaults
  const [chatConfig, setChatConfig] = useState({
    position: 'bottom-right',
    buttonPosition: 'bottom-center',
    primaryColor: '#3B82F6',
    windowTitle: 'Chat with Nia',
    subtitleText: 'AI Assistant',
    placeholderText: 'Ask me anything about our services!',
    companyName: '',
  });

  // Generate customerId from business name
  useEffect(() => {
    if (formData.businessName) {
      let processedName = formData.businessName.toLowerCase();
      
      // Replace non-alphanumeric characters with underscores
      processedName = processedName.replace(/[^a-z0-9]/g, '_');
      
      // Replace consecutive underscores with a single underscore
      processedName = processedName.replace(/_+/g, '_');
      
      // Remove leading and trailing underscores
      processedName = processedName.replace(/^_+|_+$/g, '');

      // Limit the length of the name to 20 characters
      processedName = processedName.substring(0, 20);
      
      // Remove trailing underscore if it exists after truncation
      processedName = processedName.replace(/_+$/g, '');

      // Generate a 3-character random string
      const randomString = Math.random().toString(36).substring(2, 5);
      
      // Format as trial_processedname_randomstring
      let customerId = `trial_${processedName}_${randomString}`;
      
      // Ensure we don't have more than 3 segments
      const segments = customerId.split('_');
      if (segments.length > 3) {
        // If more than 3 segments, combine the middle segments
        customerId = `trial_${segments.slice(1, -1).join('')}_${segments[segments.length-1]}`;
      }
      setFormData({
        ...formData,
        customerId
      });
      setChatConfig({
        ...chatConfig,
        customerId,
        companyName: formData.businessName
      });
    }
  }, [formData.businessName]);

  // Auto-progress through processing stages when submitting
  useEffect(() => {
    let timer;
    if (isSubmitting && processingStage < 4) {
      timer = setTimeout(() => {
        setProcessingStage(prev => prev + 1);
      }, 3000); // 3 seconds per stage
    }
    
    return () => clearTimeout(timer);
  }, [isSubmitting, processingStage]);

  // Generate script code based on configuration
  const generateScriptCode = () => {
    return `<script>
    window.NiaChat = {
        position: "${chatConfig.position}",
        buttonPosition: "${chatConfig.buttonPosition}",
        primaryColor: "${chatConfig.primaryColor}",
        customerId: "${formData.customerId}",
        windowTitle: "${chatConfig.windowTitle}",
        subtitleText: "${chatConfig.subtitleText}",
        placeholderText: "${chatConfig.placeholderText}",
        companyName: "${chatConfig.companyName || formData.businessName}",
    }
</script>
<script src="https://gclouds-gemini.web.app/embed.js" async></script>`;
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Update company name in chat config when business name changes
    if (name === 'businessName') {
      setChatConfig({
        ...chatConfig,
        companyName: value
      });
    }
  };

  // Handle chat configuration changes
  const handleConfigChange = (e) => {
    const { name, value } = e.target;
    setChatConfig({
      ...chatConfig,
      [name]: value
    });
  };

  // Handle avatar upload
  const handleAvatarUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      setFormError('Avatar image must be less than 5MB');
      return;
    }
    
    if (!file.type.startsWith('image/')) {
      setFormError('Please upload a valid image file');
      return;
    }
    
    // Store the file for later upload
    setAvatarFile(file);
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result);
      setFormError('');
    };
    reader.readAsDataURL(file);
  };

  // Handle document upload
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate files
    const validFiles = files.filter(file => {
      const validTypes = [
        'application/pdf', 
        'text/plain', 
        'text/html', 
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/msword'
      ];
      const validSize = file.size <= 25 * 1024 * 1024; // 25MB max
      
      return validTypes.includes(file.type) && validSize;
    });
    
    if (validFiles.length !== files.length) {
      setFormError('Some files were rejected. Please upload PDF, TXT, HTML, or DOC files under 25MB.');
    } else {
      setFormError('');
    }
    
    setUploadedFiles([...uploadedFiles, ...validFiles.map(file => ({
      file: file, // Store the actual File object
      name: file.name,
      size: file.size,
      type: file.type,
      uploadDate: new Date()
    }))]);
  };

  // Remove uploaded file
  const removeFile = (index) => {
    const newFiles = [...uploadedFiles];
    newFiles.splice(index, 1);
    setUploadedFiles(newFiles);
  };

  // Copy embed script to clipboard
  const copyToClipboard = () => {
    if (scriptRef.current) {
      navigator.clipboard.writeText(scriptRef.current.textContent)
        .then(() => {
          setCopySuccess(true);
          setTimeout(() => setCopySuccess(false), 2000);
        })
        .catch(err => {
          console.error('Failed to copy script: ', err);
        });
    }
  };

  // Format file size for display
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' bytes';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / 1048576).toFixed(1) + ' MB';
  };

  // Handle terms agreement checkbox
  const handleTermsChange = () => {
    const newValue = !termsAgreed;
    setTermsAgreed(newValue);
    if (newValue) {
      // Clear the error when they check the box
      setTermsError(false);
    }
  };

  // Add a separate function for uploading directly (can be used for testing)
  const uploadFileToStorage = async (file, customerId) => {
    try {
      // Get a signed URL for uploading
      const urlResult = await getDocumentUploadUrlFunction({
        customerId: customerId,
        fileName: file.name,
        contentType: file.type
      });
      
      // Use fetch to upload the file with the signed URL
      const response = await fetch(urlResult.data.uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type
        },
        body: file
      });
      
      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }
      
      return { success: true, filePath: urlResult.data.filePath };
    } catch (error) {
      console.error('File upload error:', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setProcessingStage(0);
    console.log('Form submission started');
    
    try {
    // Basic validation
    if (!formData.businessName || !formData.contactName || !formData.email || !formData.phone) {
      setFormError('Please complete all required fields');
      setIsSubmitting(false);
      return;
    }
    
    if (!termsAgreed) {
      setTermsError(true);
      setIsSubmitting(false);
      return;
    }
    
    if (uploadedFiles.length === 0) {
      setFormError('Please upload at least one document for training');
      setIsSubmitting(false);
      return;
    }
    
      // Clear previous errors
      setFormError('');
      
    // Create signup
    console.log('Creating signup...');
    let customerId;
    try {
      const result = await createSignupFunction({
        businessName: formData.businessName,
        contactName: formData.contactName,
        email: formData.email,
        phone: formData.phone,
        website: formData.website,
        industry: formData.industry,
        companySize: formData.companySize,
        salesChallenges: formData.salesChallenges,
        customerId: formData.customerId,
        chatConfig: chatConfig,
        marketingConsent: marketingConsent,
        termsAgreed: termsAgreed
      });
      
      if (!result.data || !result.data.customerId) {
        throw new Error('Invalid response from server: missing customerId');
      }
      
      customerId = result.data.customerId;
      console.log(`Signup created successfully with customerId: ${customerId}`);
      
      // Wait for the customer record to be created by the backend
      // This delay gives time for the Firestore trigger to process
      console.log('Waiting for backend processing...');
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Move to the next processing stage
      setProcessingStage(1);
      
    } catch (signupError) {
      console.error('Error creating signup:', signupError);
      setFormError(`Failed to create signup: ${signupError.message}`);
      setIsSubmitting(false);
      return;
    }

    // Now proceed with uploads
    let uploadSuccessful = true;
    
    // Upload files
    if (uploadedFiles.length > 0) {
      try {
        setUploading(true);
        console.log(`Starting upload for ${uploadedFiles.length} files`);
        
        let uploadedCount = 0;
        let failedCount = 0;
        let retryAttempts = 0;
        const maxRetries = 3;
        
        // Upload all files with retry logic
        for (const fileObj of uploadedFiles) {
          retryAttempts = 0;
          let fileUploaded = false;
          
          while (!fileUploaded && retryAttempts < maxRetries) {
            try {
              retryAttempts++;
              console.log(`Processing file: ${fileObj.name} (attempt ${retryAttempts})`);
              
              // Get signed URL for upload
              const urlResult = await getDocumentUploadUrlFunction({
                customerId: customerId,
                fileName: fileObj.name,
                contentType: fileObj.type
              });
              
              // Upload file
              const response = await fetch(urlResult.data.uploadUrl, {
                method: 'PUT',
                headers: {
                  'Content-Type': fileObj.type
                },
                body: fileObj.file
              });
              
              if (!response.ok) {
                throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
              }
              
              // If we get here, the upload was successful
              uploadedCount++;
              fileUploaded = true;
              console.log(`File ${fileObj.name} uploaded successfully`);
              
            } catch (fileError) {
              console.error(`Attempt ${retryAttempts} failed for ${fileObj.name}:`, fileError);
              
              // If it's the last retry attempt and still failed
              if (retryAttempts >= maxRetries) {
                failedCount++;
                console.error(`Failed to upload ${fileObj.name} after ${maxRetries} attempts`);
              } else {
                // Wait before retrying (exponential backoff)
                const delay = Math.min(2000 * Math.pow(2, retryAttempts - 1), 10000);
                console.log(`Waiting ${delay}ms before retrying...`);
                await new Promise(resolve => setTimeout(resolve, delay));
              }
            }
            }
          }
          
          console.log(`Upload summary: ${uploadedCount} succeeded, ${failedCount} failed`);
          
          // Move to the next processing stage
          setProcessingStage(2);
          
          if (failedCount > 0) {
            uploadSuccessful = false;
            setFormError(`${failedCount} file(s) failed to upload, but your signup was completed.`);
          }
        } catch (filesError) {
          uploadSuccessful = false;
          console.error('Error in overall file upload process:', filesError);
          setFormError('Error uploading files, but your signup was completed.');
        } finally {
          setUploading(false);
        }
      }
  
      // Upload avatar if provided
      if (avatarFile && uploadSuccessful) {
        try {
          console.log(`Processing avatar image`);
          const avatarFileName = `avatar.${avatarFile.name.split('.').pop()}`;
          
          // Get a signed URL for the avatar
          const urlResult = await getDocumentUploadUrlFunction({
            customerId: customerId,
            fileName: `images/${avatarFileName}`,
            contentType: avatarFile.type,
            isAvatar: true
          });
          
          // Upload the avatar
          const response = await fetch(urlResult.data.uploadUrl, {
            method: 'PUT',
            headers: {
              'Content-Type': avatarFile.type
            },
            body: avatarFile
          });
          
          if (!response.ok) {
              throw new Error(`Avatar upload failed with status: ${response.status}`);
            }

            console.log(`Avatar uploaded successfully`);
            setChatConfig({
              ...chatConfig,
              avatarPath: urlResult.data.filePath
            });
        } catch (avatarError) {
          console.error('Error uploading avatar:', avatarError);
          // Continue with signup even if avatar fails
        }
      }
      
      // Wait a bit to show the processing stages animation
      await new Promise(resolve => setTimeout(resolve, 4000));
      
      // Move to the final processing stage
      setProcessingStage(4);
      
      // Complete the signup process
      console.log('Signup process completed');
      setFormSubmitted(true);
      
      // Wait a moment and then move to success step
      setTimeout(() => {
        setActiveStep(5);
        setIsSubmitting(false);
      }, 2000);
      
    } catch (error) {
      // Catch any unhandled errors
      console.error('Unhandled error in signup process:', error);
      setFormError(`An unexpected error occurred: ${error.message}`);
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 text-white min-h-screen py-16 px-6">
      {/* Header Section */}
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Sign Up for Your <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Nia Trial</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Get started with your free 14-day Salesforce AI agent trial. Register, Customise and deploy in minutes.
          </p>
        </div>
        
        {/* Progress Steps - only shown when not submitting */}
      {!isSubmitting && (
        <div className="mb-10">
          <div className="flex justify-between items-center max-w-4xl mx-auto">
            {[1, 2, 3, 4, 5].map((step) => (
              <div key={step} className="flex flex-col items-center">
                <div 
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold mb-2 
                    ${activeStep >= step 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-slate-700 text-slate-300'}`}
                >
                  {step < activeStep ? <Check className="w-5 h-5" /> : step}
                </div>
                <span className={`text-sm ${activeStep >= step ? 'text-blue-400' : 'text-slate-400'}`}>
                  {step === 1 && 'Business Info'}
                  {step === 2 && 'Upload Content'}
                  {step === 3 && 'Customise Nia'}
                  {step === 4 && 'Agreement'}
                  {step === 5 && 'Get Code'}
                </span>
              </div>
            ))}
          </div>
          <div className="h-1 max-w-4xl mx-auto bg-slate-700 mt-4 mb-12 relative">
            <div 
              className="h-full bg-blue-600 absolute top-0 left-0 transition-all duration-500"
              style={{ width: `${(activeStep - 1) * 25}%` }}
            ></div>
          </div>
        </div>
        )}
        
        {/* Main content area with grid layout */}
        <div className="max-w-7xl mx-auto">
          {/* When submitting, switch to a different layout that emphasises the animation */}
          {isSubmitting ? (
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold mb-2">Processing Your Registration</h2>
                <p className="text-slate-300">Setting up your AI Sales Assistant. This may take a few moments...</p>
              </div>
              
              {/* Center the animation during submission */}
              <div className="mx-auto max-w-lg">
                <ThrongletPipeline 
                  stage={activeStep} 
                  processingStage={processingStage} 
                  isSubmitting={true} 
                />
              </div>
              
              {/* Processing information card */}
              <div className="mt-8 bg-slate-800/50 backdrop-blur-sm rounded-lg p-6 border border-slate-700/50 max-w-3xl mx-auto">
                <div className="flex items-start">
                  <Clock className="w-6 h-6 text-blue-400 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-lg mb-2">Backend Processing in Progress</h3>
                    <p className="text-slate-300 mb-4">
                      Your Nia instance is being created. Here's what's happening behind the scenes:
                    </p>
                    <ol className="space-y-2 pl-5 list-decimal text-slate-300">
                      <li className={processingStage >= 0 ? 'text-blue-300' : ''}>
                        Storing your documents safely
                        {processingStage === 0 && <span className="inline-block ml-2 animate-pulse">...</span>}
                        {processingStage > 0 && <Check className="inline-block ml-2 w-4 h-4 text-green-400" />}
                      </li>
                      <li className={processingStage >= 1 ? 'text-blue-300' : ''}>
                        Organising your information
                        {processingStage === 1 && <span className="inline-block ml-2 animate-pulse">...</span>}
                        {processingStage > 1 && <Check className="inline-block ml-2 w-4 h-4 text-green-400" />}
                      </li>
                      <li className={processingStage >= 2 ? 'text-blue-300' : ''}>
                        Setting up your trial account
                        {processingStage === 2 && <span className="inline-block ml-2 animate-pulse">...</span>}
                        {processingStage > 2 && <Check className="inline-block ml-2 w-4 h-4 text-green-400" />}
                      </li>
                      <li className={processingStage >= 3 ? 'text-blue-300' : ''}>
                        Crating AI brain
                        {processingStage === 3 && <span className="inline-block ml-2 animate-pulse">...</span>}
                        {processingStage > 3 && <Check className="inline-block ml-2 w-4 h-4 text-green-400" />}
                      </li>
                      <li className={processingStage >= 4 ? 'text-blue-300' : ''}>
                        Almost ready! Creating your chat window code
                        {processingStage === 4 && <span className="inline-block ml-2 animate-pulse">...</span>}
                        {processingStage > 4 && <Check className="inline-block ml-2 w-4 h-4 text-green-400" />}
                      </li>
                    </ol>
                  </div>
                </div>
                
                <div className="mt-6 text-center">
                  <p className="text-slate-400 text-sm">
                    Please do not close this window while processing. This typically takes 1-2 minutes depends number of files.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Form Container - Takes 2/3 of space on desktop */}
              <div className="lg:col-span-2">
          <form onSubmit={handleSubmit} className="bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 shadow-xl p-8">
            {/* Step 1: Business Information */}
            {activeStep === 1 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold mb-6">Business Information</h2>
                
                {/* Two column layout */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Business Name *
                    </label>
                    <input 
                      type="text" 
                      name="businessName"
                      value={formData.businessName}
                      onChange={handleInputChange}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Contact Person's Name *
                    </label>
                    <input 
                      type="text"
                      name="contactName"
                      value={formData.contactName}
                      onChange={handleInputChange}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Email Address *
                    </label>
                    <input 
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Phone Number *
                    </label>
                    <input 
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Website URL *
                    </label>
                    <input 
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white"
                      placeholder="https://www.example.com"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Industry/Vertical *
                    </label>
                    <select
                      name="industry"
                      value={formData.industry}
                      onChange={handleInputChange}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white"
                      required
                    >
                      <option value="">Select your industry</option>
                      <option value="Technology">Technology</option>
                      <option value="Finance">Finance & Banking</option>
                      <option value="Healthcare">Healthcare</option>
                      <option value="Education">Education</option>
                      <option value="Retail">Retail & E-commerce</option>
                      <option value="Manufacturing">Manufacturing</option>
                      <option value="Legal">Legal</option>
                      <option value="RealEstate">Real Estate</option>
                      <option value="Hospitality">Hospitality & Tourism</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Company Size *
                    </label>
                    <select
                      name="companySize"
                      value={formData.companySize}
                      onChange={handleInputChange}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white"
                      required
                    >
                      <option value="">Select company size</option>
                      <option value="1-10">1-10 employees</option>
                      <option value="11-50">11-50 employees</option>
                      <option value="51-200">51-200 employees</option>
                      <option value="201-500">201-500 employees</option>
                      <option value="501-1000">501-1000 employees</option>
                      <option value="1000+">1000+ employees</option>
                    </select>
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Main Sales Challenges (Optional)
                    </label>
                    <textarea
                      name="salesChallenges"
                      value={formData.salesChallenges}
                      onChange={handleInputChange}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white h-24"
                      placeholder="Describe the sales challenges you're looking to solve with Nia..."
                    ></textarea>
                  </div>
                </div>
                
                {/* Generated Customer ID */}
                <div className="bg-slate-700/50 p-4 rounded-lg border border-slate-600">
                  <div className="flex items-start">
                    <Info className="w-5 h-5 text-blue-400 mt-0.5 mr-2 flex-shrink-0" />
                    <div>
                      <h3 className="font-medium text-white">Your Customer ID</h3>
                      <p className="text-sm text-slate-300 mb-2">This will be your unique identifier for the Nia integration.</p>
                      <code className="bg-slate-800 text-blue-300 px-2 py-1 rounded text-sm">
                        {formData.customerId || 'trial_your_business_name'}
                      </code>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Step 2: Document Upload */}
            {activeStep === 2 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold mb-6">Upload Your Sales Materials</h2>
                
                <p className="text-slate-300 mb-6">
                  Nia needs to learn about your business to provide accurate responses to your customers. 
                  Upload your sales materials, product documentation, FAQs, and other relevant documents to train your AI assistant.
                </p>
                
                {/* Upload area */}
                <div className="border-2 border-dashed border-slate-600 rounded-lg p-8 text-center">
                  <Upload className="w-12 h-12 text-blue-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Upload Your Documents</h3>
                  <p className="text-slate-300 mb-4">Drag and drop your files here, or click to browse</p>
                  <p className="text-sm text-slate-400 mb-6">Supports PDF, TXT, HTML, DOCX, and PPTX up to 25MB each</p>
                  <input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    multiple
                    onChange={handleFileUpload}
                    accept=".pdf,.txt,.html,.doc,.docx"
                  />
                  <label
                    htmlFor="file-upload"
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors inline-block cursor-pointer"
                  >
                    Select Files
                  </label>
                </div>
                
                {/* Uploaded files list */}
                {uploadedFiles.length > 0 && (
                  <div className="mt-8">
                    <h3 className="text-lg font-semibold mb-4">Uploaded Files ({uploadedFiles.length})</h3>
                    <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
                      {uploadedFiles.map((file, index) => (
                        <div 
                          key={index}
                          className="bg-slate-700 rounded-lg p-3 flex items-center justify-between"
                        >
                          <div className="flex items-center">
                            <FileText className="w-5 h-5 text-blue-400 mr-3" />
                            <div>
                              <p className="font-medium">{file.name}</p>
                              <p className="text-sm text-slate-400">
                                {formatFileSize(file.size)} • {new Date(file.uploadDate).toLocaleString()}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="text-slate-400 hover:text-red-400"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {uploading && (
                  <div className="bg-blue-500/10 rounded-lg p-4 flex items-center">
                    <div className="animate-spin mr-3">
                      <svg className="w-5 h-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                    <span>Uploading files...</span>
                  </div>
                )}
                
                {/* Training info */}
                <div className="bg-slate-700/50 p-4 rounded-lg border border-slate-600 mt-6">
                  <div className="flex items-start">
                    <Clock className="w-5 h-5 text-blue-400 mt-0.5 mr-2 flex-shrink-0" />
                    <div>
                      <h3 className="font-medium text-white">Important: Training Takes Time</h3>
                      <p className="text-sm text-slate-300">
                        After uploading your materials, it will take up to 24 hours for Nia to process and learn from your content.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Step 3: Customise Nia */}
            {activeStep === 3 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold mb-6">Customise Your AI Assistant</h2>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Customisation options */}
                  <div>
                    <div className="space-y-6">
                      {/* Avatar upload */}
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Assistant Avatar (Optional)
                        </label>
                        <div className="flex items-center space-x-4">
                          <div className="w-16 h-16 rounded-full bg-slate-700 flex items-center justify-center overflow-hidden">
                            {avatarPreview ? (
                              <img 
                                src={avatarPreview} 
                                alt="Avatar Preview" 
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <Camera className="w-6 h-6 text-slate-400" />
                            )}
                          </div>
                          <div>
                            <input
                              type="file"
                              id="avatar-upload"
                              className="hidden"
                              onChange={handleAvatarUpload}
                              accept="image/*"
                            />
                            <label
                              htmlFor="avatar-upload"
                              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg font-medium transition-colors inline-block cursor-pointer text-sm"
                            >
                              <ImageIcon className="w-4 h-4 inline mr-2" />
                              Choose Image
                            </label>
                            <p className="text-xs text-slate-400 mt-1">Square image, 5MB max</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Color picker */}
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Primary Color
                        </label>
                        <div className="flex items-center space-x-3">
                          <input 
                            type="color" 
                            name="primaryColor"
                            value={chatConfig.primaryColor}
                            onChange={handleConfigChange}
                            className="w-10 h-10 rounded overflow-hidden cursor-pointer"
                          />
                          <input 
                            type="text" 
                            name="primaryColor"
                            value={chatConfig.primaryColor}
                            onChange={handleConfigChange}
                            className="bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white w-32"
                          />
                        </div>
                      </div>
                      
                      {/* Position selector */}
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Chat Window Position
                        </label>
                        <select
                          name="position"
                          value={chatConfig.position}
                          onChange={handleConfigChange}
                          className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white"
                        >
                          <option value="bottom-right">Bottom Right</option>
                          <option value="bottom-left">Bottom Left</option>
                          <option value="top-right">Top Right</option>
                          <option value="top-left">Top Left</option>
                        </select>
                      </div>
                      
                      {/* Button position selector */}
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Chat Button Position
                        </label>
                        <select
                          name="buttonPosition"
                          value={chatConfig.buttonPosition}
                          onChange={handleConfigChange}
                          className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white"
                        >
                          <option value="bottom-center">Bottom Center</option>
                          <option value="bottom-right">Bottom Right</option>
                          <option value="bottom-left">Bottom Left</option>
                          <option value="top-right">Top Right</option>
                          <option value="top-left">Top Left</option>
                          <option value="top-center">Top Center</option>
                        </select>
                      </div>
                      
                      {/* Window title */}
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Chat Window Title
                        </label>
                        <input 
                          type="text" 
                          name="windowTitle"
                          value={chatConfig.windowTitle}
                          onChange={handleConfigChange}
                          className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white"
                          placeholder="Chat with Nia"
                        />
                      </div>
                      
                      {/* Subtitle */}
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Assistant Subtitle
                        </label>
                        <input 
                          type="text" 
                          name="subtitleText"
                          value={chatConfig.subtitleText}
                          onChange={handleConfigChange}
                          className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white"
                          placeholder="AI Assistant"
                        />
                      </div>
                      
                      {/* Placeholder text */}
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Input Placeholder Text
                        </label>
                        <input 
                          type="text" 
                          name="placeholderText"
                          value={chatConfig.placeholderText}
                          onChange={handleConfigChange}
                          className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white"
                          placeholder="Ask me anything about our services!"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Live preview */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center">
                      <Layout className="w-5 h-5 mr-2 text-blue-400" />
                      Live Preview
                    </h3>
                    
                    <div className="bg-slate-900 rounded-xl border border-slate-700 p-6">
                      {/* Preview of chat interface */}
                      <div className="bg-slate-800 rounded-lg overflow-hidden border border-slate-700 mb-6">
                        <div 
                          className="flex items-center p-3 border-b border-slate-700"
                          style={{ backgroundColor: chatConfig.primaryColor + '20' }}
                        >
                          <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center mr-3 overflow-hidden">
                            {avatarPreview ? (
                              <img 
                                src={avatarPreview} 
                                alt="Avatar" 
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div 
                                className="w-full h-full flex items-center justify-center text-white"
                                style={{ backgroundColor: chatConfig.primaryColor }}
                              >
                                N
                              </div>
                            )}
                          </div>
                          <div>
                            <h4 className="font-medium text-white">{chatConfig.windowTitle || 'Chat with Nia'}</h4>
                            <p className="text-xs text-slate-300">{chatConfig.subtitleText || 'AI Assistant'}</p>
                          </div>
                        </div>
                        
                        <div className="p-3 h-64 overflow-y-auto space-y-4">
                          <div className="flex items-start space-x-2">
                            <div className="w-8 h-8 rounded-full flex-shrink-0 overflow-hidden">
                              {avatarPreview ? (
                                <img 
                                  src={avatarPreview} 
                                  alt="Avatar" 
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div 
                                  className="w-full h-full flex items-center justify-center text-white"
                                  style={{ backgroundColor: chatConfig.primaryColor }}
                                >
                                  N
                                </div>
                              )}
                            </div>
                            <div className="bg-slate-700 rounded-lg p-3 max-w-[85%]">
                              <p className="text-sm">Hello! I'm Nia, your AI assistant for {chatConfig.companyName || formData.businessName || 'your company'}. How can I help you today?</p>
                            </div>
                          </div>
                          
                          <div className="flex justify-end">
                            <div 
                              className="rounded-lg p-3 max-w-[85%] text-white"
                              style={{ backgroundColor: chatConfig.primaryColor }}
                            >
                              <p className="text-sm">Can you tell me more about your services?</p>
                            </div>
                          </div>
                          
                          <div className="flex items-start space-x-2">
                            <div className="w-8 h-8 rounded-full flex-shrink-0 overflow-hidden">
                              {avatarPreview ? (
                                <img 
                                  src={avatarPreview} 
                                  alt="Avatar" 
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div 
                                  className="w-full h-full flex items-center justify-center text-white"
                                  style={{ backgroundColor: chatConfig.primaryColor }}
                                >
                                  N
                                </div>
                              )}
                            </div>
                            <div className="bg-slate-700 rounded-lg p-3 max-w-[85%]">
                              <p className="text-sm">I'd be happy to tell you about {chatConfig.companyName || formData.businessName || 'our'} services! We specialise in...</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="p-3 border-t border-slate-700">
                          <div className="flex space-x-2">
                            <input 
                              type="text" 
                              className="flex-1 bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-sm"
                              placeholder={chatConfig.placeholderText || 'Type your message...'}
                              disabled
                            />
                            <button 
                              className="p-2 rounded-lg text-white"
                              style={{ backgroundColor: chatConfig.primaryColor }}
                              disabled
                            >
                              <ChevronRight className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Button preview */}
                      <div className="text-center">
                        <p className="text-sm text-slate-400 mb-4">Chat Button Preview</p>
                        <div className="inline-flex items-center justify-center">
                          <button 
                            className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg"
                            style={{ backgroundColor: chatConfig.primaryColor }}
                            disabled
                          >
                            <MessageSquare className="w-5 h-5 text-white" />
                          </button>
                        </div>
                        <p className="text-xs text-slate-500 mt-2">Position: {chatConfig.buttonPosition}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Step 4: Agreement */}
            {activeStep === 4 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold mb-6">Terms and Pricing Agreement</h2>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    {/* Terms and conditions */}
                    <div className="bg-slate-700/50 rounded-lg border border-slate-600 p-6 mb-6">
                      <h3 className="text-lg font-semibold mb-4">Terms & Conditions</h3>
                      <div className="h-60 overflow-y-auto pr-2 text-sm text-slate-300 space-y-4">
                        <p>
                          By signing up for the Salesforce AI Agent, you agree to the following terms and conditions:
                        </p>
                        <p>
                          <strong>1. Free Trial Period:</strong> The 14-day trial is provided at no cost. No payment information is required to start your trial.
                        </p>
                        <p>
                          <strong>2. Pricing After Trial:</strong> After the trial period, the pricing model outlined in the ROI Calculator will apply, including:
                        </p>
                        <ul className="list-disc pl-6 space-y-2">
                            <li>
                                <strong>Simple, fair pricing based on your business size:</strong>
                                <ul className="list-[circle] pl-6 mt-2 space-y-1">
                                    <li><strong>Small Businesses</strong> (&lt;£500K annual revenue): <strong>£999</strong></li>
                                    <li><strong>Medium/Large Businesses</strong>: Custom quote (based on sales process complexity)</li>
                                </ul>
                                <p className="mt-2 text-sm text-gray-600">
                                    Why the difference? Larger businesses often need sophisticated customisations, integrations, custom training, and advanced workflows. We'll always discuss costs upfront, no surprises.
                                </p>
                            </li>
                            <li>
                              <strong>Infrastructure costs:</strong> 
                              <ul className="list-[circle] pl-6 mt-1 space-y-1">
                                <li>Runs on <strong>Google Cloud Platform</strong> (enterprise-grade secure infrastructure)</li>
                                <li>Billed separately based on actual usage</li>
                                <li>Automatically scales down to zero when idle</li>
                                <li>You only pay for what you use</li>
                              </ul>
                            </li>
                              <li>
                                <strong>Commission Structure:</strong>
                                <ul className="list-[circle] pl-6 mt-2 space-y-3">
                                  <li>
                                    <strong>First Purchase (15% Commission):</strong><br />
                                    15% commission on the first sale from Nia acquired customers (excluding VAT).
                                  </li>
                                  <li>
                                    <strong>All Future Purchases (8% Commission):</strong><br />
                                    8% on every future purchase they make; whether days, months, or years later.<br />
                                    Every subsequent order from that same customer triggers an 8% commission, regardless of timing or amount.
                                  </li>
                                  <li>
                                    <strong>Critical Implications:</strong>
                                    <ul className="list-[square] pl-6 mt-1 space-y-2">
                                      <li><strong>Attribution Matters:</strong> Only sales tied to Nia's influence trigger commissions.</li>
                                      <li><strong>VAT Exclusion:</strong> Commissions are calculated on the pre-tax amount.</li>
                                      <li><strong>Lifetime Value Focus:</strong> Nia is incentivised to drive quality leads that generate repeat business.</li>
                                      <li><strong>No Double-Dipping:</strong> You only pay either initial (15%) or follow-up (8%), never both on the same sale.</li>
                                    </ul>
                                  </li>
                                  <li>
                                    <strong>Examples:</strong>
                                    <ul className="list-[square] pl-6 mt-1 space-y-1">
                                      <li>New customer buys £1,000 product → £150 commission</li>
                                      <li>Same customer buys £300 accessory 3 months later → £24 commission</li>
                                      <li>Same customer renews £800 service 1 year later → £64 commission</li>
                                    </ul>
                                  </li>
                                </ul>
                              </li>
                              <li>
                                No commission is charged on sales not attributed to Nia
                              </li>
                        </ul>
                        <p>
                          For the ROI Calculator, please visit <a href="/nia#roi" className="text-blue-400 hover:underline">ROI Calculator page</a>.
                        </p>
                        <p className="mt-4">
                          <strong>3. Data Usage & Protection:</strong>
                        </p>
                        <ul className="list-disc pl-6 mt-2 space-y-2">
                          <li>All uploaded materials are used exclusively to train your Nia instance for accurate business responses</li>
                          <li>Fully compliant with <strong>UK GDPR</strong> regulations</li>
                          <li>Enterprise-grade security:
                            <ul className="list-[circle] pl-6 mt-1 space-y-1">
                              <li>Data stored in <strong>EU-based</strong> Google Cloud services</li>
                              <li><strong>Encrypted</strong> at rest and in transit</li>
                              <li>Strict <strong>access controls</strong> and audit logging</li>
                            </ul>
                          </li>
                        </ul>
                        <p>
                          <strong>4. Ownership:</strong> You retain ownership of all content you provide. gClouds does not claim ownership of your materials.
                        </p>
                        <p>
                          <strong>5. Service Availability:</strong> While we strive for 99.9% uptime, we cannot guarantee uninterrupted service.
                        </p>
                        <p>
                          <strong>6. Cancellation:</strong> You may cancel your service at any time with 30 days' notice.
                        </p>
                        <p>
                          <strong>7. Acceptable Use:</strong> The service may not be used for illegal activities or content that violates our acceptable use policy.
                        </p>
                        <p>
                          <strong>8. Support:</strong> Technical support is available during standard business hours.
                        </p>
                        <p>
                           <strong>9. Attribution Policy: </strong>
                                  Sales are attributed to Nia when:
                                  <ul className="list-[square] pl-6 mt-1 space-y-1">
                                    <li>A prospect engages with Nia through:
                                      <ul className="list-[circle] pl-6 mt-1 space-y-1">
                                        <li>Chat conversations, <strong>or</strong></li>
                                        <li>Scheduled meetings, <strong>or</strong></li>
                                        <li>Sent quotes/emails, <strong>or</strong></li>
                                        <li>Call interactions (coming soon), <strong>or</strong></li>
                                        <li>Any other platform-assisted engagement</li>
                                      </ul>
                                    </li>
                                    <li>And subsequently becomes a customer</li>
                                  </ul>
                                <li className="text-sm text-gray-600">
                                  (Single interaction sufficient • Includes future features • Tracked through our system)
                                </li>
                        </p>
                      </div>
                    </div>

                    {/* Checkbox agreements */}
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <div className="flex items-center h-5 mt-1">
                          <input
                            id="terms"
                            type="checkbox"
                            checked={termsAgreed}
                            onChange={handleTermsChange}
                            className="w-4 h-4 text-blue-600 rounded border-slate-600 focus:ring-blue-500"
                          />
                        </div>
                        <label htmlFor="terms" className="ml-3 text-sm text-slate-300">
                          I have read and agree to the Terms and Conditions, including the pricing structure after the trial period ends.
                        </label>
                      </div>

                      <div className="flex items-start">
                        <div className="flex items-center h-5 mt-1">
                          <input
                            id="marketing"
                            type="checkbox"
                            checked={marketingConsent}
                            onChange={() => setMarketingConsent(!marketingConsent)}
                            className="w-4 h-4 text-blue-600 rounded border-slate-600 focus:ring-blue-500"
                          />
                        </div>
                        <label htmlFor="marketing" className="ml-3 text-sm text-slate-300">
                          I consent to receive marketing communications from gClouds about products, services, and special offers (optional).
                        </label>
                      </div>
                    </div>
                          
                          {/* Terms error message */}
                          {termsError && (
                            <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 mt-4">
                              <div className="flex items-start">
                                <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 mr-2 flex-shrink-0" />
                                <p className="text-red-300">You must agree to the terms and conditions</p>
                              </div>
                            </div>
                          )}
                  </div>

                  {/* Pricing summary */}
                  <div>
                    <div className="bg-blue-500/10 rounded-xl p-6 border border-blue-500/50">
                      <h3 className="text-2xl font-semibold text-blue-400 mb-6">Pricing Summary</h3>
                      <div className="space-y-4">
                        <div className="flex justify-between items-center pb-4 border-b border-slate-700">
                          <div>
                            <h4 className="font-medium">14-Day Trial</h4>
                            <p className="text-sm text-slate-400">Full access to all features</p>
                          </div>
                          <span className="text-green-400 font-bold">FREE</span>
                        </div>

                        <div className="flex justify-between items-center pb-4 border-b border-slate-700">
                          <div>
                            <h4 className="font-medium">One-Time Setup Fee</h4>
                            <p className="text-sm text-slate-400">After trial period</p>
                          </div>
                          <span className="font-bold">From £999</span>
                        </div>

                        <div className="flex justify-between items-center pb-4 border-b border-slate-700">
                          <div>
                            <h4 className="font-medium">Success-Based Model</h4>
                            <p className="text-sm text-slate-400">Only pay when you succeed</p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium">15% on Initial Sale Amount</p>
                            <p className="text-sm text-slate-400">8% on Follow-up Sales</p>
                          </div>
                        </div>

                        <div className="pt-2">
                          <div className="flex items-center space-x-2 text-blue-400">
                            <CreditCard className="w-5 h-5" />
                            <span className="font-medium">No credit card required for trial</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-slate-700/50 rounded-lg border border-slate-600 p-4 mt-6">
                      <div className="flex items-start">
                        <Info className="w-5 h-5 text-blue-400 mt-0.5 mr-2 flex-shrink-0" />
                        <div>
                          <h3 className="font-medium text-white">No Payment Information Required</h3>
                          <p className="text-sm text-slate-300">
                            Your trial is completely free. We'll contact you before the trial ends to discuss continuing with the paid service.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Step 5: Success and Code Generation */}
            {activeStep === 5 && (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-green-400" />
                  </div>
                  <h2 className="text-2xl font-bold mb-2">Your Nia Setup is Complete!</h2>
                  <p className="text-slate-300 max-w-2xl mx-auto">
                    We're processing your sales materials and preparing your AI assistant.
                  </p>
                </div>
                
                <div className="bg-slate-700/50 rounded-lg border border-slate-600 p-6 max-w-3xl mx-auto">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <Type className="w-5 h-5 mr-2 text-blue-400" />
                    Your Integration Code
                  </h3>
                  <p className="text-slate-300 mb-4">
                    Copy and paste this code into the <code className="text-blue-300">&lt;head&gt;</code> section of your website:
                  </p>
                  
                  <div className="relative">
                    <div className="bg-slate-800 rounded-lg p-4 overflow-x-auto text-sm">
                      <pre ref={scriptRef} className="text-blue-300 whitespace-pre-wrap">
                        {generateScriptCode()}
                      </pre>
                    </div>
                    <button
                      type="button"
                      onClick={copyToClipboard}
                      className="absolute top-3 right-3 p-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
                      title="Copy to clipboard"
                    >
                      {copySuccess ? (
                        <CheckCircle className="w-5 h-5 text-green-400" />
                      ) : (
                        <Copy className="w-5 h-5 text-slate-300" />
                      )}
                    </button>
                  </div>
                  
                  <div className="mt-4 text-slate-300 text-sm">
                    <p className="mb-2">
                      <strong>Important:</strong> This code will not fully activate until your AI assistant training is complete. We'll email you when it's ready to use.
                    </p>
                    <p>
                      <strong>Note:</strong> This code will disappear if you close this window - please use the <span className="font-mono bg-slate-700 px-1 py-0.5 rounded">Copy to clipboard</span> button to save it.
                    </p>
                  </div>
                </div>
                
                <div className="bg-blue-500/10 rounded-lg border border-blue-500/50 p-4 max-w-3xl mx-auto">
                  <div className="flex items-start">
                    <AlertTriangle className="w-5 h-5 text-blue-400 mt-0.5 mr-2 flex-shrink-0" />
                    <div>
                      <h3 className="font-medium text-white">Next Steps</h3>
                      <p className="text-sm text-slate-300 mb-2">
                        We're working on training your AI assistant with your sales materials. Here's what happens next:
                      </p>
                      <ol className="text-sm text-slate-300 list-decimal pl-5 space-y-1">
                        <li>We process your uploaded documents and train your Nia instance (within 24 hours)</li>
                        <li>You'll receive an email notification when Nia is ready</li>
                        <li>
                          <span className="font-medium">Your 14-day trial has now begun</span>
                          <ul className="list-disc pl-5 mt-1 text-slate-400 text-xs">
                            <li>No SLA or guarantees are provided during trial</li>
                            <li>We may terminate service at any time without notice</li>
                            <li>No updates or support included in trial period</li>
                          </ul>
                        </li>
                        <li>We'll contact you before the trial ends to discuss continuing the service</li>
                      </ol>
                    </div>
                  </div>
                </div>
                
                <div className="text-center mt-8">
                  <button
                    type="button"
                    onClick={() => navigate('/')}
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors"
                  >
                    Return to Homepage
                  </button>
                </div>
              </div>
            )}
            
            {/* Form error message */}
            {formError && (
              <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 mt-6">
                <div className="flex items-start">
                  <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 mr-2 flex-shrink-0" />
                  <p className="text-red-300">{formError}</p>
                </div>
              </div>
            )}
            
            {/* Navigation buttons */}
            {activeStep < 5 && (
              <div className="flex justify-between mt-8">
                <button
                  type="button"
                  onClick={() => setActiveStep(Math.max(1, activeStep - 1))}
                  className={`px-6 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg font-medium transition-colors ${activeStep === 1 ? 'invisible' : ''}`}
                >
                  Previous
                </button>

                {activeStep < 4 ? (
                  <button
                    type="button"
                    onClick={() => {
                      // Simple validation for each step
                      if (activeStep === 1) {
                        if (!formData.businessName || !formData.contactName || !formData.email || !formData.phone || !formData.website || !formData.industry || !formData.companySize) {
                          setFormError('Please complete all required fields');
                          return;
                        }
                      } else if (activeStep === 2) {
                        if (uploadedFiles.length === 0) {
                          setFormError('Please upload at least one document for training');
                          return;
                        }
                      }

                      setFormError('');
                      setActiveStep(activeStep + 1);
                    }}
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors"
                  >
                    Next
                  </button>
                ) : (
                  <button
                    type="submit"
                    className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                      isSubmitting 
                        ? 'bg-slate-500 cursor-not-allowed' // Gray out when submitting
                        : 'bg-green-600 hover:bg-green-700' // Normal state
                    }`}
                    disabled={isSubmitting} // Disable while submitting
                  >
                    {isSubmitting ? (
                      <>
                        <div className="inline-block animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                        Processing...
                      </>
                    ) : (
                      'Complete Signup'
                    )}
                  </button>
                )}
              </div>
            )}

          </form>
            
                </div>
        {/* ThrongletPipeline Animation - Takes 1/3 of space on desktop */}
        <div className="hidden lg:block">
          <ThrongletPipeline stage={activeStep} />
        </div>
        {/* Mobile view of ThrongletPipeline - Only shown on mobile, below the form */}
        <div className="lg:hidden mt-6">
          <ThrongletPipeline stage={activeStep} />
        </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NiaSignupAnimated;